/**
 * Messages API Slice
 * Injects message/feedback endpoints into the base API slice
 */

import { apiSlice } from "../api/apiSlice";
import { generatePaginationCacheTag, CACHE_TIME } from "../api/apiHelpers";

// Date filter parameter types
export interface DateFilterParams {
  mode: "single" | "range";
  date?: string;
  startDate?: string;
  endDate?: string;
}

export const messagesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get feedback details with pagination
     * Used for displaying feedback messages table
     * venueId is extracted from JWT token by backend
     */
    getFeedbackDetails: builder.query<
      { success: boolean; data: any[]; pagination: any },
      {
        startDate: string;
        endDate: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ startDate, endDate, page = 1, limit = 50 }) => ({
        url: `/api/analytics/feedback-details`,
        params: { startDate, endDate, page, limit },
      }),
      providesTags: (result, error, { startDate, endDate, page = 1 }) => [
        { type: "Messages", id: "FEEDBACK_DETAILS" },
        {
          type: "Messages",
          id: generatePaginationCacheTag("FEEDBACK", page, startDate, endDate),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.SHORT,
    }),

    /**
     * Get payment details with pagination
     * Used for displaying payment transactions table
     * venueId is extracted from JWT token by backend
     * NOTE: This endpoint logically belongs in paymentsApiSlice but kept here for backward compatibility
     */
    getPaymentDetails: builder.query<
      { success: boolean; data: any[]; pagination: any },
      {
        startDate: string;
        endDate: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ startDate, endDate, page = 1, limit = 50 }) => ({
        url: `/api/analytics/payment-details`,
        params: { startDate, endDate, page, limit },
      }),
      providesTags: (result, error, { startDate, endDate, page = 1 }) => [
        { type: "Messages", id: "PAYMENT_DETAILS" },
        { type: "Payments", id: "LIST" },
        {
          type: "Payments",
          id: generatePaginationCacheTag("PAYMENT", page, startDate, endDate),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.SHORT,
    }),
  }),
});

// Export hooks for use in components
export const { useGetFeedbackDetailsQuery, useGetPaymentDetailsQuery } =
  messagesApiSlice;
